import { zodResolver } from "@hookform/resolvers/zod";
import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouteContext,
} from "@tanstack/react-router";
import { useAtom, useAtomValue } from "jotai";
import { type ReactNode, useMemo, type JSX } from "react";
import { useForm } from "react-hook-form";

import { graphql } from "@repo/graphql-types/gql";
import ArrowRight from "@repo/icons/default/ArrowRight";
import { CpsButtonText } from "corpus";

import { FirstScheduleDurationField } from "@/components/onboarding/first-schedule-duration-field";
import { PriceSuggestionCardContent } from "@/components/onboarding/price-suggestion-card-content";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import {
  FormControl,
  FormField,
  FormHandlerSubmit,
  FormItem,
  FormRoot,
  FormSubmitButton,
} from "@/components/ui/form/form";
import { InputCurrency } from "@/components/ui/input/input-currency";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";
import {
  CardSelectDescription,
  CardSelectItem,
  CardSelectItemContainer,
  CardSelectRoot,
  CardSelectTitle,
} from "@/components/ui/select-input/card-select";
import { useAppointmentDurationLimits } from "@/hooks/use-appointment-duration-limits";
import { useRecordFirstScheduleStop } from "@/hooks/use-first-schedule-history";
import { useGraphQL } from "@/hooks/use-graphql";
import { firstScheduleAtom } from "@/lib/atoms/first-schedule-atom";
import {
  buildFirstAppointmentFormSchema,
  type FirstAppointmentFormFields,
  parseMaskedPrice,
} from "@/lib/form-schemas/first-schedule-appointment-types-schema";
import {
  getPriceSuggestions,
  type PriceStrategy,
  type PriceSuggestions,
} from "@/lib/helpers/appointment-price-suggestions";
import { toMaskedCurrency } from "@/lib/helpers/currency";
import {
  FIRST_SCHEDULE_STEPS,
  FOLLOW_UP_APPOINTMENT_TYPE_NAME,
  getFirstScheduleProgress,
  getRecommendedPriceStrategy,
  MIGRATE_OWN_OFFICE_ANSWER,
  ONBOARDING_FLOW_STEP_CODES,
  syncFollowUpSuggestedPrice,
  toDraftDuration,
} from "@/lib/helpers/first-schedule-flow";
import { requireCreatedSchedules } from "@/lib/helpers/first-schedule-guards";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const FirstScheduleFirstAppointmentQuery = graphql(/* GraphQL */ `
  query FirstScheduleFirstAppointmentQuery($codAgendas: [Int!]!) {
    usuariosAgendas(where: { codUsuarioAgenda: { _in: $codAgendas } }) {
      codUsuarioAgenda
      appointmentTypes: tbUsuariosAgendasCompromissos(
        where: { ativo: { _eq: true }, UsuarioCompromisso: { ativo: { _eq: true } } }
      ) {
        appointmentType: UsuarioCompromisso {
          codUsuarioCompromisso
          nome
          duracao
        }
      }
    }
  }
`);

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const FirstScheduleOnboardingAnswersQuery = graphql(/* GraphQL */ `
  query FirstScheduleOnboardingAnswersQuery {
    membro {
      fluxos {
        etapas {
          codEtapa
          resposta
        }
      }
    }
  }
`);

const PRICE_STRATEGIES: PriceStrategy[] = ["attract", "average", "premium"];

const DEFAULT_DURATION = "01:00";

interface OnboardingAnswers {
  livanceExpectation?: string;
  averageDuration?: string;
}

const useOnboardingAnswers = (): { answers: OnboardingAnswers; isPending: boolean } => {
  const { data, isPending } = useGraphQL(FirstScheduleOnboardingAnswersQuery);

  const answers = useMemo(() => {
    const steps = (data?.membro?.fluxos ?? []).flatMap((flow) => flow?.etapas ?? []);
    const answerOf = (codEtapa: number): string | undefined =>
      steps.find((step) => step?.codEtapa === codEtapa && step.resposta?.value)?.resposta
        ?.value;

    return {
      livanceExpectation: answerOf(ONBOARDING_FLOW_STEP_CODES.livanceExpectations),
      averageDuration: answerOf(ONBOARDING_FLOW_STEP_CODES.appointmentAverageDuration),
    };
  }, [data]);

  return { answers, isPending };
};

const FirstScheduleFirstAppointmentPage = (): JSX.Element => {
  const form = useAtomValue(firstScheduleAtom);
  useRecordFirstScheduleStop("firstAppointment");

  const { data, isPending } = useGraphQL(FirstScheduleFirstAppointmentQuery, {
    codAgendas: form.codAgendas,
  });
  const { answers, isPending: isAnswersPending } = useOnboardingAnswers();

  if (isPending || isAnswersPending) {
    return (
      <FirstAppointmentLayout>
        <FullPageSpinner />
      </FirstAppointmentLayout>
    );
  }

  const currentDuration = data?.usuariosAgendas[0]?.appointmentTypes.find(
    (item) => item.appointmentType.nome !== FOLLOW_UP_APPOINTMENT_TYPE_NAME,
  )?.appointmentType.duracao;

  return (
    <FirstAppointmentLayout>
      <FirstAppointmentForm
        livanceExpectation={answers.livanceExpectation}
        defaultDuration={
          answers.averageDuration ??
          (currentDuration ? toDraftDuration(currentDuration) : DEFAULT_DURATION)
        }
      />
    </FirstAppointmentLayout>
  );
};

const FirstAppointmentLayout = ({ children }: { children: ReactNode }): JSX.Element => {
  const { flags } = useRouteContext({ from: "__root__" });

  return (
    <RoundedLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.BackButton />
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
        <FlowHeader.ProgressBar
          progress={getFirstScheduleProgress(
            FIRST_SCHEDULE_STEPS.firstAppointment,
            flags["exibe-configuracao-tipos-de-atendimento-na-primeira-agenda"],
          )}
        />
      </FlowHeader>

      <Page className="flex flex-col pb-0 px-6" showCustomerIoContainer={false}>
        {children}
      </Page>
    </RoundedLayout>
  );
};

interface FirstAppointmentFormProps {
  livanceExpectation?: string;
  defaultDuration: string;
}

const FirstAppointmentForm = ({
  livanceExpectation,
  defaultDuration,
}: FirstAppointmentFormProps): JSX.Element => {
  const navigate = useNavigate();
  const { user } = useRouteContext({ from: "__root__" });
  const [draft, setDraft] = useAtom(firstScheduleAtom);
  const durationLimits = useAppointmentDurationLimits();

  const specialty = user.especialidades?.[0]?.Especialidade.nome ?? undefined;
  const suggestions = useMemo(() => getPriceSuggestions(specialty), [specialty]);
  const recommendedStrategy = getRecommendedPriceStrategy(livanceExpectation);
  const isMigrating = livanceExpectation === MIGRATE_OWN_OFFICE_ANSWER;

  const schema = useMemo(
    () => buildFirstAppointmentFormSchema(durationLimits),
    [durationLimits],
  );

  const savedDraft = draft.firstAppointment;

  const formMethods = useForm<FirstAppointmentFormFields>({
    resolver: zodResolver(schema),
    defaultValues: {
      strategy: savedDraft?.strategy ?? (isMigrating ? "custom" : undefined),
      customPrice:
        savedDraft?.strategy === "custom"
          ? toMaskedCurrency(savedDraft.price)
          : undefined,
      duration: savedDraft?.duration ?? defaultDuration,
    },
  });

  const selectedStrategy = formMethods.watch("strategy");

  const handleSubmit = (values: FirstAppointmentFormFields): void => {
    const price =
      values.strategy === "custom"
        ? parseMaskedPrice(values.customPrice)
        : suggestions[values.strategy];

    setDraft((prev) => ({
      ...prev,
      firstAppointment: { strategy: values.strategy, price, duration: values.duration },
      followUp: syncFollowUpSuggestedPrice(
        prev.followUp,
        prev.firstAppointment?.price,
        price,
      ),
    }));

    void navigate({ to: "/onboarding/first-schedule/follow-up" });
  };

  const handleSetUpLater = (): void => {
    void navigate({
      to: "/onboarding/first-schedule/success",
      search: { appointmentTypes: "skipped" },
    });
  };

  const priceInput = (title: string): JSX.Element => (
    <FormField
      control={formMethods.control}
      name="customPrice"
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <InputCurrency title={title} required {...field} />
          </FormControl>
        </FormItem>
      )}
    />
  );

  return (
    <FormRoot {...formMethods}>
      <FormHandlerSubmit
        handleSubmit={handleSubmit}
        className="w-full flex-1 justify-between gap-4"
      >
        <div className="flex flex-col gap-4">
          <div>
            <CardSelectTitle>
              Quanto seus pacientes vão pagar na primeira consulta?
            </CardSelectTitle>
            <CardSelectDescription>
              {specialty
                ? `Veja o que profissionais de ${specialty} cobram, com base em mais de 2 milhões de atendimentos na Livance.`
                : "Veja o que os profissionais cobram, com base em mais de 2 milhões de atendimentos na Livance."}
            </CardSelectDescription>
          </div>

          {isMigrating ? (
            <>
              {priceInput("Quanto você cobra hoje?")}
              <PriceSuggestionReference suggestions={suggestions} />
            </>
          ) : (
            <FormField
              control={formMethods.control}
              name="strategy"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div>
                      <CardSelectRoot
                        name="first-appointment-price"
                        defaultValue={field.value}
                        onChange={field.onChange}
                      >
                        <CardSelectItemContainer>
                          {PRICE_STRATEGIES.map((strategy) => (
                            <CardSelectItem
                              key={strategy}
                              value={strategy}
                              className="justify-start"
                            >
                              <PriceSuggestionCardContent
                                strategy={strategy}
                                price={suggestions[strategy]}
                                recommended={strategy === recommendedStrategy}
                              />
                            </CardSelectItem>
                          ))}
                          <CardSelectItem value="custom" className="justify-start">
                            Definir outro valor
                          </CardSelectItem>
                        </CardSelectItemContainer>
                      </CardSelectRoot>
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
          )}

          {!isMigrating && selectedStrategy === "custom"
            ? priceInput("Valor da primeira consulta")
            : null}

          <FormField
            control={formMethods.control}
            name="duration"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <FirstScheduleDurationField
                    value={field.value}
                    onChange={field.onChange}
                    maxDurationInMinutes={durationLimits.maxDurationInMinutes}
                    drawerTitle="Informe a duração da primeira consulta"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="sticky bottom-0 flex flex-col items-center gap-1 bg-white pt-4 pb-4">
          <FormSubmitButton color="secondary" className="w-full">
            <div className="flex flex-row items-center justify-center gap-2">
              Continuar
              <ArrowRight size={24} aria-hidden="true" />
            </div>
          </FormSubmitButton>
          <CpsButtonText type="button" size="xs" onClick={handleSetUpLater}>
            Definir depois
          </CpsButtonText>
        </div>
      </FormHandlerSubmit>
    </FormRoot>
  );
};

interface PriceSuggestionReferenceProps {
  suggestions: PriceSuggestions;
}

const PriceSuggestionReference = ({
  suggestions,
}: PriceSuggestionReferenceProps): JSX.Element => (
  <section aria-label="Valores de referência" className="flex flex-col gap-3">
    <p className="text-sm font-medium text-neutral-600">Valores de referência</p>
    {PRICE_STRATEGIES.map((strategy) => (
      <div
        key={strategy}
        className="rounded-lg border-2 border-neutral-100 bg-neutral-100 px-4 py-4"
      >
        <PriceSuggestionCardContent strategy={strategy} price={suggestions[strategy]} />
      </div>
    ))}
  </section>
);

export const Route = createFileRoute("/onboarding/first-schedule/first-appointment")({
  beforeLoad: ({ context }) => {
    if (!context.flags["exibe-configuracao-tipos-de-atendimento-na-primeira-agenda"]) {
      redirect({ to: "/onboarding/first-schedule/success", throw: true });
    }
    requireCreatedSchedules();
  },
  component: FirstScheduleFirstAppointmentPage,
});
